import React, { useState, useEffect, useRef } from "react";
import { Document, Page } from "react-pdf";
import { Box, IconButton, Typography, Fade } from "@mui/material";
import { NavigateBefore, NavigateNext } from "@mui/icons-material";

const PDFSlideViewer = ({ fileUrl, onLastSlideReached }) => {
  const [numPages, setNumPages] = useState(null);
  const [page, setPage] = useState(1);
  const [isLastPage, setIsLastPage] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const controlsTimeoutRef = useRef(null);

  const onLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setIsLastPage(numPages === 1);
  };

  const setupAutoHideControls = () => {
    clearTimeout(controlsTimeoutRef.current);
    setShowControls(true);
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 2000);
  };

  const goToNextSlide = () => {
    if (page < numPages) {
      const nextPage = page + 1;
      setPage(nextPage);
      setIsLastPage(nextPage === numPages);

      if (nextPage === numPages && onLastSlideReached) {
        onLastSlideReached();
      }
    }
    setupAutoHideControls();
  };

  const goToPreviousSlide = () => {
    if (page > 1) {
      const prevPage = page - 1;
      setPage(prevPage);
      setIsLastPage(false);
    }
    setupAutoHideControls();
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === "ArrowRight") {
        goToNextSlide();
      } else if (e.key === "ArrowLeft") {
        goToPreviousSlide();
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [page, numPages]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        height: "600px",
        padding: "10px",
        overflow: "auto",
      }}
    >
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          position: "relative",
        }}
      >
        {/* Bottom Navigation Bar */}
        <Fade in={showControls}>
          <Box
            sx={{
              position: "absolute",
              bottom: "55px",
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              alignItems: "center",
              gap: 2,
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              padding: "8px 16px",
              borderRadius: "25px",
              zIndex: 10,
            }}
          >
            {/* Left Arrow */}
            <IconButton
              onClick={goToPreviousSlide}
              disabled={page === 1}
              size="small"
              sx={{
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
                "&:disabled": {
                  opacity: 0.3,
                },
              }}
            >
              <NavigateBefore fontSize="small" />
            </IconButton>

            {/* Page Counter */}
            <Typography
              variant="body2"
              sx={{
                color: "white",
                fontWeight: "bold",
                fontSize: "14px",
                minWidth: "60px",
                textAlign: "center",
              }}
            >
              {page} / {numPages || "..."}
            </Typography>

            {/* Right Arrow */}
            <IconButton
              onClick={goToNextSlide}
              disabled={page === numPages}
              size="small"
              sx={{
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
                "&:disabled": {
                  opacity: 0.3,
                },
              }}
            >
              <NavigateNext fontSize="small" />
            </IconButton>
          </Box>
        </Fade>

        {/* PDF Content */}
        <Document file={fileUrl} onLoadSuccess={onLoadSuccess}>
          <Page
            loading={<div>Loading PPT...</div>}
            pageNumber={page}
            width={Math.min(900, window.innerWidth - 50)}
            style={{ boxShadow: "0 4px 8px rgba(0,0,0,0.1)" }}
            onMouseMove={setupAutoHideControls}
          />
        </Document>
      </div>
    </div>
  );
};

export default PDFSlideViewer;
